<template>
  <div class="unscheduled-treatment-pg">
    <!-- Follow-ups redesign (docs/plans/2026-10-02-followups-redesign-plan.md, Phase 2). Super
         Admins get the new branch unless they switch; offices follow STAGE.overdueRecall (src/utils/followups/uiVersion.js);
         the v-else branch below is the classic page, unchanged. The page still owns its data,
         filters, lists and goals — the new branch only binds them to the shared components. -->
    <div v-if="fuUseNewUi" class="fu-or" data-test="fu-overdue-recall">
      <followups-ui-toggle target="old" page-key="overdueRecall" @toggle="fuSetUi" />
      <followups-page-shell
        title="Overdue recall"
        lede="Patients whose recall is past due and who have nothing on the schedule. Pulled live from Open Dental."
      >
        <template #actions>
          <followups-filter-button
            v-if="isAptReminderPaidFor"
            label="Reminder report"
            icon="chart"
            :chevron="false"
            data-test="fu-reminder-report"
            @click="showRecallReportDrawer = true"
          />
          <followups-filter-button
            label="Your lists"
            icon="list"
            data-test="fu-lists-menu"
            :popover="fuListsPopover"
            @select="fuLoadList"
          />
          <list-download-menu
            queue="overdueRecall"
            :rows="overdueRecallData || []"
            title="Overdue Recall"
            :ctx="{
              dateFrom: dateFrom,
              dateTo: dateTo,
              dateFormatStr: dateFormatStr,
              currencySymbol: allSettings && allSettings.GENERAL_CURRENCY_SYMBOL ? allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue : '$',
              remindersPaidFor: isAptReminderPaidFor,
              siteName: getSite(siteID) ? (getSite(siteID).SiteName || getSite(siteID).Name || '') : '',
            }"
          />
        </template>
        <template #tabs>
          <followups-tabs :tabs="fuTabs" active="overdueRecall" />
        </template>

        <ErrorDisplay/>
        <followups-summary-cards
          :due-today="fuCounts ? fuCounts.dueToday : 0"
          :past-due="fuCounts ? fuCounts.pastDue : 0"
          :due-active="fuView === 'now'"
          :goals="fuGoals"
          @show-due="fuView = 'now'"
          @show-all="fuView = 'all'"
        />
        <followups-queue
          queue="overdueRecall"
          mode="page"
          :rows="overdueRecallData || []"
          :selected="selectedPatients"
          :busy="busy"
          :view.sync="fuView"
          @counts="fuCounts = $event"
          @toggle-row="row => updateSelectedPatients(selectedPatients, row)"
          @toggle-all="fuToggleAll"
          @call-goals-updated="callGoalsUpdated"
          @clear-selection="clearSelection"
          @add-to-existing-list="fuOpenListDialog('add')"
          @create-new-list="fuOpenListDialog('create')"
        >
          <template #switches>
            <followups-switch
              v-model="frequencyof2peryear"
              label="Only 2×/yr preventive coverage"
              aria-describedby="fu_help_frequency"
              data-test="fu-freq2"
            />
            <span id="fu_help_frequency" class="fu-or__help" tabindex="0" aria-label="About this filter">
              <fu-icon name="info" :size="16" />
            </span>
            <b-popover target="fu_help_frequency" triggers="hover focus" placement="bottom">
              <template v-slot:title>Filtering Patients with Preventive Frequency</template>
              <div class="help_popover">
                <h4 class="fw-semi-bold">If the Filter is "On"</h4>
                <p>Only patients who have insurance whose preventive frequency allows for at least 2 visits per year are shown.</p>
                <p><span class="fw-semi-bold">Note:</span> Regardless of whether the filter is on or off, patients who have no insurance attached are always shown on the list.</p>
                <hr/>
                <h4 class="fw-semi-bold">If the Filter is "Off"</h4>
                <p>All patients due for recall are shown in the list.</p>
              </div>
            </b-popover>
          </template>
          <template #filters>
            <followups-filter-button
              icon="cal"
              :label="fuDateLabel"
              data-test="fu-filter-date"
              :popover="fuDatePopover"
              @apply="fuApplyDates"
            />
            <followups-filter-button
              icon="users"
              :label="fuProvidersLabel"
              data-test="fu-filter-providers"
              :popover="fuProvidersPopover"
              @apply="fuApplyProviders"
            />
          </template>
          <template #filters-end>
            <followups-filter-button
              icon="sliders"
              variant="ghost"
              :chevron="false"
              :active="fuMoreCount > 0"
              :label="fuMoreCount ? `More filters · ${fuMoreCount}` : 'More filters'"
              data-test="fu-more-filters"
              @click="fuMoreOpen = true"
            />
          </template>
        </followups-queue>
      </followups-page-shell>

      <followups-more-filters
        v-if="fuMoreOpen"
        :sections="fuMoreSections"
        :value="fuMoreValue"
        @apply="fuApplyMore"
        @close="fuMoreOpen = false"
      />
      <followups-list-dialog
        v-if="fuDialog"
        :kind="fuDialog"
        :lists="fuDialogLists"
        :patients="fuDialogPatients"
        :busy="fuSaving"
        @save="fuSaveList"
        @remove-patient="id => removeSelectedPatient(selectedPatients, id)"
        @close="fuDialog = null"
      />
    </div>
    <template v-else>
    <followups-ui-toggle target="new" page-key="overdueRecall" @toggle="fuSetUi" />
    <h2 class="page-title">
      Overdue Recall
    </h2>
    <OverdueRecallSkeleton v-if="busy" />
    <b-row v-show="!busy">
      <!-- Filter Panel -->
      <b-col sm="12" md="12" lg="8" xl="6" class="px-xl-3 mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-filter section-icon"></i>
              Filter Patients
            </h5>
          </div>
          <div class="filter-content">
            <b-row>
              <b-col md="6">
                <div class="filter-group">
                  <label class="filter-label">Recall Due Date From</label>
                  <DatePicker
                    lang="en"
                    :placeholder="datepickerText"
                    input-class="form-control"
                    v-model="dateFrom"
                    :format="dateFormatStr"
                    class="w-100"
                  >
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>

                <div class="filter-group">
                  <label class="filter-label">To</label>
                  <DatePicker
                    lang="en"
                    :placeholder="datepickerText"
                    input-class="form-control"
                    v-model="dateTo"
                    :format="dateFormatStr"
                    class="w-100"
                  >
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Patients Older Than</label>
                  <b-form-input v-model="patientsOlderThan" placeholder="Enter age"></b-form-input>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Providers</label>
                  <div v-b-tooltip.hover="selectedProvider && selectedProvider.length > 2 ? selectedProvider.map(p => providerCustomLabel(p)).join(', ') : ''">
                  <multiselect
                    v-if="providers"
                    v-model="selectedProvider"
                    :options="getProviderOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="providerCustomLabel"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    label="FName"
                    track-by="ProvNum"
                    aria-placeholder="Select a Provider"
                  >
                    <template slot="singleLabel" slot-scope="props">
                      <span class="custom__tag">
                        <img class="option__image" src="../../assets/icons-user-40.png" alt="Team member image" />
                        <span>{{ providerCustomLabel(props.option) }}</span>
                        <i tabindex="1" class="multiselect__tag-icon" @click="removeProvider(props.option)"></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <img class="option__image" src="../../assets/icons-user-40.png" alt="Team member image" />
                      <div class="option__desc">
                        <span class="option__title">{{ providerCustomLabel(props.option) }}</span>
                      </div>
                    </template>
                  </multiselect>
                  </div>
                </div>

              </b-col>

              <b-col md="6">
                <div class="filter-group">
                  <label class="filter-label">Filter by Plans</label>
                  <b-form-group class="abc-radio mb-2">
                    <b-form-radio-group
                      id="include_exclude_codes"
                      v-model="includeOrExcludePlan"
                      :options="includeExcludeOptions"
                      name="radio-options"
                    ></b-form-radio-group>
                  </b-form-group>

                  <div v-b-tooltip.hover="selectedPlans && selectedPlans.length > 2 ? selectedPlans.map(p => p.CarrierName).join(', ') : ''">
                  <multiselect
                    v-if="getPlanOptions"
                    v-model="selectedPlans"
                    :options="getPlanOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="planOptionsWithCount"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    label="CarrierName"
                    track-by="CarrierNum"
                    @search-change="searchPlans"
                    aria-placeholder="Add Options"
                    class="w-100"
                  >
                    <template slot="tag" slot-scope="props">
                      <span class="custom__tag">
                        <span>{{props.option.CarrierName}}</span>
                        <i tabindex="1" class="multiselect__tag-icon" @click="removePlan(props.option)"></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <div class="option__title d-flex">
                          <span class="option-code">{{planOptionsWithCount(props.option)}}</span>
                        </div>
                      </div>
                    </template>
                  </multiselect>
                  </div>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Recall Types</label>
                  <div v-b-tooltip.hover="selectedRecallTypes && selectedRecallTypes.length > 2 ? selectedRecallTypes.map(rt => rt.Description).join(', ') : ''">
                  <multiselect
                    v-if="getRecallTypeOptions.length > 0"
                    v-model="selectedRecallTypes"
                    :options="getRecallTypeOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="opt => opt.Description"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    label="Description"
                    track-by="RecallTypeNum"
                    aria-placeholder="Select Recall Types"
                  >
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <span class="option__title">{{ props.option.Description }}</span>
                      </div>
                    </template>
                  </multiselect>
                  </div>
                </div>

                <button class="apply-filters-btn mt-3" @click="applyFilter">
                  <i class="fa fa-search"></i>
                  Apply Filters
                </button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>

      <!-- Lists Panel -->
      <b-col sm="12" md="5" lg="4" xl="3" class="px-xl-3 mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-th-list section-icon"></i>
              Your Lists
            </h5>
          </div>
          <div class="filter-content">
            <div v-if="this.getLists && this.getLists.length > 0">
              <div class="filter-group">
                <label class="filter-label">Overdue Recall Lists</label>
                <b-dropdown id="simple-select" :text="selectedListName" variant="default" class="w-100" boundary="window">
                  <b-dropdown-item-button v-for="list in this.getLists" :key="list.ListID" @click="loadList(list.ListID, list.Name)">
                    <i class="glyphicon glyphicon-th-list text-secondary mr-xs mb-xs" /> {{list.Name}}
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>

              <!-- Recent Lists -->
              <div class="recent-lists mt-3" v-if="recentLists.length > 0">
                <label class="filter-label">Recently Created</label>
                <div class="recent-list-items">
                  <div
                    v-for="list in recentLists"
                    :key="'recent-' + list.ListID"
                    class="recent-list-item"
                    @click="loadList(list.ListID, list.Name)"
                  >
                    <i class="fa fa-clock-o text-muted"></i>
                    <span class="list-name">{{ list.Name }}</span>
                  </div>
                </div>
              </div>

              <!-- List Stats -->
              <div class="list-stats mt-3">
                <div class="stat-item">
                  <i class="fa fa-folder-open text-muted"></i>
                  <span class="stat-text">{{ getLists.length }} list{{ getLists.length !== 1 ? 's' : '' }} created</span>
                </div>
              </div>
            </div>

            <div v-if="!this.getLists || this.getLists.length === 0" class="empty-lists-state">
              <div class="empty-icon">
                <i class="fa fa-list-alt"></i>
              </div>
              <h6 class="empty-title">Create your first List</h6>
              <p class="empty-description">Organize patients into focused groups for better follow-up.</p>

              <div class="list-ideas">
                <p class="ideas-label">Ideas to get started:</p>
                <ul class="ideas-list">
                  <li><i class="fa fa-check text-success"></i> Patients overdue 6+ months</li>
                  <li><i class="fa fa-check text-success"></i> Specific insurance carriers</li>
                  <li><i class="fa fa-check text-success"></i> Patients by hygienist</li>
                </ul>
              </div>

              <p class="empty-hint">
                <i class="fa fa-hand-pointer-o"></i>
                Select patients below and click "Create New List"
              </p>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Goals Panel -->
      <b-col sm="12" md="7" lg="12" xl="3" class="px-xl-3 mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-bullseye section-icon"></i>
              Call Goals
            </h5>
          </div>
          <div class="filter-content" v-if="allSettings">
            <div class="goal-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="goal-label"><strong>Daily</strong> goal</span>
                <span class="goal-count">{{dailyAttempts}} / {{allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL && allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue}}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div role="progressbar" :aria-valuenow="dailyPercent" class="progress-bar bg-success" :style="dailyStyle"></div>
              </div>
            </div>

            <div class="goal-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="goal-label"><strong>Weekly</strong> goal</span>
                <span class="goal-count">{{weeklyAttempts}} / {{allSettings.OVERDUE_RECALL_WEEKLY_CALLS_GOAL && allSettings.OVERDUE_RECALL_WEEKLY_CALLS_GOAL.PropertyValue}}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div role="progressbar" :aria-valuenow="weeklyPercent" class="progress-bar bg-info" :style="weeklyStyle"></div>
              </div>
            </div>

            <div class="goal-item">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="goal-label"><strong>Monthly</strong> goal</span>
                <span class="goal-count">{{monthlyAttempts}} / {{allSettings.OVERDUE_RECALL_MONTHLY_CALLS_GOAL && allSettings.OVERDUE_RECALL_MONTHLY_CALLS_GOAL.PropertyValue}}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div role="progressbar" :aria-valuenow="monthlyPercent" class="progress-bar bg-primary" :style="monthlyStyle"></div>
              </div>
            </div>

            <!-- Motivational Message -->
            <div class="goal-motivation mt-3">
              <div v-if="dailyPercent >= 100" class="motivation-badge success">
                <i class="fa fa-trophy"></i>
                <span>Daily goal achieved!</span>
              </div>
              <div v-else-if="dailyPercent >= 75" class="motivation-badge almost">
                <i class="fa fa-fire"></i>
                <span>Almost there! {{ allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue - dailyAttempts }} more to hit daily goal</span>
              </div>
              <div v-else class="motivation-badge info">
                <i class="fa fa-phone"></i>
                <span>{{ allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue - dailyAttempts }} calls to reach today's goal</span>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-3" v-show="!busy">
      <b-col class="px-xl-3">
        <div class="modern-table-panel">
          <div class="panel-header d-flex justify-content-between align-items-center flex-wrap">
            <h5 class="section-title no-subtitle">
              <i class="fa fa-list section-icon"></i>
              Overdue Recall Patients
            </h5>
            <div class="panel-controls">
              <div class="d-flex align-items-center">
                <!-- Recall Reminder Text Report. Gated on the same paid-for flag as the
                     "Auto Reminders Sent" column: a site that isn't paying for automated
                     reminders has no send history to show. -->
                <button
                  v-if="isAptReminderPaidFor"
                  type="button"
                  id="recall_text_report"
                  class="recall-report-btn mr-3"
                  @click="showRecallReportDrawer = true"
                >
                  <i class="fa fa-bar-chart"></i>
                </button>
                <b-tooltip target="recall_text_report" triggers="hover" placement="top">
                  View report of Recall Reminder Messages sent
                </b-tooltip>
                <b-form-checkbox
                  id="frequencyof2peryear"
                  v-model="frequencyof2peryear"
                  name="checkbox-1"
                  switch
                  size="lg"
                  class="mr-2"
                />
                <span class="filter-label-text">Only show patients with Preventive Freq of 2 per Year</span>
                <span class="ml-2" id="help_frequency"><i class="fa fa-question-circle text-warning"></i></span>
                <b-popover target="help_frequency" triggers="hover" placement="top">
                  <template v-slot:title>Filtering Patients with Preventive Frequency</template>
                  <div class="help_popover">
                    <h4 class="fw-semi-bold">If the Filter is "On"</h4>
                    <p>Only patients who have insurance whose preventive frequency allows for at least 2 visits per year are shown.</p>
                    <p><span class="fw-semi-bold">Note:</span> Regardless of whether the filter is on or off, patients who have no insurance attached are always shown on the list.</p>
                    <hr/>
                    <h4 class="fw-semi-bold">If the Filter is "Off"</h4>
                    <p>All patients due for recall are shown in the list.</p>
                  </div>
                </b-popover>
              </div>
              <div class="mt-2">
                <list-download-menu
                  queue="overdueRecall"
                  :rows="overdueRecallData || []"
                  title="Overdue Recall"
                  :ctx="{
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                    dateFormatStr: dateFormatStr,
                    currencySymbol: allSettings && allSettings.GENERAL_CURRENCY_SYMBOL ? allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue : '$',
                    remindersPaidFor: isAptReminderPaidFor,
                    siteName: getSite(siteID) ? (getSite(siteID).SiteName || getSite(siteID).Name || '') : '',
                  }"
                />
              </div>
            </div>
          </div>
          <div class="table-content">
            <ErrorDisplay/>
            <AdminTable
              v-if="overdueRecallData"
              :data="overdueRecallData"
              :columns="columns"
              :options="options"
              :extra="teamioTableExtraParams"
              :selectedPatients="selectedPatients"
              :showBulkActions="true"
              :tableTitle="'Overdue Recall Patients'"
              @call-goals-updated="callGoalsUpdated"
              @clear-selection="clearSelection"
              @add-to-existing-list="addToExistingList"
              @create-new-list="createNewList"
            />
          </div>
        </div>
      </b-col>
    </b-row>
    </template>
    <!-- The new branch's ONE slide-out outlet (the follow-up drawer). The legacy branch's outlet
         lives inside AdminTable; v-if / v-else keeps the two from ever being mounted together. -->
    <slideout-panel v-if="fuUseNewUi"></slideout-panel>

    <!-- Recall Reminder Text Report slide-out. Sits outside the v-show="!busy" rows
         above so a background refresh can't hide an open report. v-if (not a prop) so
         the component - and the global mixin's created() hook - costs nothing until
         someone actually opens the report. -->
    <RecallReminderReportDrawer
      v-if="showRecallReportDrawer"
      @close="showRecallReportDrawer = false"
    />

    <!-- Modal -->
    <b-modal
      ref="create-list"
      id="create-list"
      title="Create List: Overdue Recall"
      body-class="bg-white mb-0"
      scrollable
    >
      <b-row>
        <b-col>
          <b-form-group
            horizontal
            label="List Title:"
            label-for="normal-field"
            :label-cols="3"
            breakpoint="md"
            class="mt-2"
          >
            <b-form-input
              v-model="listTitle"
              type="text"
              id
              style="border: 1px solid #e8e8e8;"
              placeholder="My Sample List"
              @input="resetError"
            />
          </b-form-group>

          <b-form-group
            horizontal
            label="Description:"
            label-for="normal-field"
            :label-cols="3"
            breakpoint="md"
            class="mt-2"
          >
            <textarea v-model="listDescription" name placeholder="Describe your list here (optional)" class="form-control"></textarea>
          </b-form-group>

          <!--

          <div class="routine-form">
            <label>Assign to:</label>
            <multiselect
              v-if="this.activeEmployees"
              v-model="listAssignedTo"
              :options="this.activeEmployees"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              :custom-label="employeesCustomLabel"
              label="Name"
              track-by="Name"
              aria-placeholder
              disabled
            >
              <template slot="singleLabel" slot-scope="props">
                <span class="custom__tag">
                  <img
                    class="option__image"
                    src="../../assets/icons-user-40.png"
                    alt="Team member image"
                  />
                  <span>{{ props.option.Name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <img
                  class="option__image"
                  src="../../assets/icons-user-40.png"
                  alt="Team member image"
                />
                <div class="option__desc">
                  <span class="option__title">{{ props.option.Name }}</span>
                </div>
              </template>
            </multiselect>
          </div>
           -->

          <h6 style="margin-top: 30px;">
            <span class="font-weight-bold mr-1">{{selectedPatients.length}}</span>
            <span class="fw-semi-bold text-success">Patients</span> selected
          </h6>

          <div class="table-responsive patient-list-table mt-1" v-if="selectedPatients.length > 0">
            <v-client-table :data="selectedPatients" :columns="patientColumns" :options="patientOptions">
              <div slot="h__PatNum"></div>
              <div slot="h__Patient">Patient</div>
              <div slot="PatNum" slot-scope="props">
                <span class="fa fa-trash-o delete_icon text-danger" @click="removeSelectedPatient(selectedPatients, props.row.PatNum)"></span>
              </div>
            </v-client-table>
          </div>
        </b-col>
      </b-row>

      <div slot="modal-footer" class="w-100">
        <b-button
          variant="danger"
          size="sm"
          class="float-right"
          @click="hideModal('create-list')"
        >Cancel</b-button>
        <b-button
          variant="success"
          size="sm"
          class="float-right mr-3"
          @click="saveList"
          :disabled="this.loading || this.somePatientsBelongsToList"
        >Create & Save List</b-button>
      </div>
      <ErrorDisplay/>
    </b-modal>

    <b-modal
      ref="add-to-existing-list"
      id="add-to-existing-list"
      title="Add to Existing List: Overdue Recall"
      body-class="bg-white mb-0"
    >
      <b-row>
        <b-col>
          <label class="mt-2 mb-1">Select List to add patients to:</label>
          <div class="routine-form mt-1">
            <multiselect
              v-model="selectedList"
              :options="getLists"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              label="Name"
              track-by="Name"
              aria-placeholder
              @select="resetError"
            >
              <template slot="singleLabel" slot-scope="props">
                <span class="custom__tag">
                  <span>{{ props.option.Name }}</span>
                  <!-- <i tabindex="1" class="multiselect__tag-icon" @click="removeMember(props.option)"></i> -->
                  <!-- <span class="custom__remove" @click="removeMember(props.option)">X</span> -->
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <!-- <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image" /> -->
                <div class="option__desc">
                  <span class="option__title">{{ props.option.Name }}</span>
                </div>
              </template>
            </multiselect>
          </div>
          <!-- routine-form -->

          <h6 style="margin-top: 30px;">
            <span class="font-weight-bold mr-1">{{selectedPatients.length}}</span>
            <span class="fw-semi-bold text-success">Patients</span> selected
          </h6>

          <div class="table-responsive patient-list-table mt-1" v-if="selectedPatients.length > 0">
            <v-client-table :data="selectedPatients" :columns="patientColumns" :options="patientOptions">
              <div slot="h__PatNum">#</div>
              <div slot="h__Patient">First Name</div>
              <div slot="PatNum" slot-scope="props">
                <span class="fa fa-trash-o delete_icon text-danger" @click="removeSelectedPatient(selectedPatients, props.row.PatNum)"></span>
              </div>
            </v-client-table>
          </div>
        </b-col>
      </b-row>

      <div slot="modal-footer" class="w-100">
        <b-button
          variant="danger"
          size="sm"
          class="float-right"
          @click="selectedList=[]; hideModal('add-to-existing-list')"
        >Cancel</b-button>
        <b-button
          variant="success"
          size="sm"
          class="float-right mr-3"
          @click="saveList($event, selectedList.ListID)"
          :disabled="loading || somePatientsBelongsToList"
        >Add to List & Save</b-button>
      </div>
      <ErrorDisplay/>
    </b-modal>

  </div>
</template>

<script>
/* eslint-disable */

import Vue from "vue";
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import Widget from "@/components/Widget/Widget";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import $ from "jquery";
import CommonQueries from '../../sql/queries';
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import mixin from '../../mixin.js';
import AdminTable from '@/pages/Admin/AdminTable';
import OverdueRecallSkeleton from './OverdueRecallSkeleton.vue';
import RecallReminderReportDrawer from './components/RecallReminderReportDrawer.vue';
// Follow-ups redesign (Phase 2) — the new branch of the template. See fuUseNewUi.
import FollowupsQueue from '@/components/Followups/FollowupsQueue.vue';
import FollowupsPageShell from '@/components/Followups/FollowupsPageShell.vue';
import FollowupsTabs from '@/components/Followups/FollowupsTabs.vue';
import FollowupsSummaryCards from '@/components/Followups/FollowupsSummaryCards.vue';
import FollowupsFilterButton from '@/components/Followups/FollowupsFilterButton.vue';
import FollowupsMoreFilters from '@/components/Followups/FollowupsMoreFilters.vue';
import FollowupsListDialog from '@/components/Followups/FollowupsListDialog.vue';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import FuIcon from '@/components/Followups/FuIcon.vue';
import ListDownloadMenu from '@/components/Followups/ListDownloadMenu.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { visibleTabs, sortListsNewestFirst } from '@/utils/followups/pageChrome.js';

export default {
  name: 'OverdueRecall',
  components: {
    DatePicker, Multiselect, Widget, ErrorDisplay, AdminTable, OverdueRecallSkeleton, RecallReminderReportDrawer,
    FollowupsQueue, FollowupsPageShell, FollowupsTabs, FollowupsSummaryCards, FollowupsFilterButton,
    FollowupsMoreFilters, FollowupsListDialog, FollowupsSwitch, FollowupsUiToggle, FuIcon,
    ListDownloadMenu,
  },
  props: ['lists', 'list'],
  mixins: [mixin],

  created() {
    /*
      The very first time page is loaded, default provider is not available. So we'll watch it and call init then. However, on the 2nd load (if you move away from this page
      and come back), the variable IS avialable and we can just call init here.
    */
    if(this.getDefaultProvider) {
      //console.log(`it is available so will call init now`);
      this.init();
    }
  },

  mounted() {
    //this.initCharts();
  },

  data() {
    return {
      results: [],
      includeOrExcludePlan: '',
      includeExcludeOptions:[
        {text:'Include', value: 'INCLUDE'},
        {text:'Exclude', value: 'EXCLUDE'}
      ], 
      frequencyof2peryear: false,
      showRecallReportDrawer: false,
      teamioTableExtraParams: {
        detailPageName: 'OverdueRecallDetails',
      },
      overdueRecallStatusColors: {
        'Not Home. Call Again': 'primary',
        'Call Back, Not Ready': 'danger',
        'Patient Will Call Us' : 'FF6384',
        'Wait. See Notes': 'purple',
        'Couldn\'t Leave Voicemail': 'pink',
        'Left Voicemail' : 'f87979',
        'Left Message with Fam': 'orange',
      },
      dateFrom: '',
      dateTo: '',
      datepickerText: "",
      patientsOlderThan: null,
      color: {
        hex: "#cccccc"
      },
      isPickerActive: false,
      selectedPlans: [],
      selectedProvider: [],
      selectedRecallTypes: [],
      selectedList : 'Select',
      selectedListName: "Select",
      listTitle: '',
      listDescription: '',
      selectedList: [],
      selectedPatients: [],
      //columns: ['id', 'Patient', 'DateDue', 'RecallInfo', 'Carrier', 'Note', 'status','FollowUpActionPlan', 'Attempts'],
      //columns: ['id', 'Patient', 'DateDue', 'RecallInfo', 'Carrier', 'AutoRemindersSent', 'Status','FollowUpActionPlan', 'Attempts'],
      //// isMainChecked: false,
      patientColumns:['PatNum', 'Patient'],
      patientOptions: {
        filterable: false,
        skin:'table',
        sortable: [],
        perPage: 10,
        sortByColumn: false,
        pagination: { chunk: 10, dropdown: false },
        columnsClasses: {PatNum:'text-center'}
      },
      busy: false,
      // Follow-ups redesign (new branch only) — UI state; the data and filters above stay the truth.
      fuUiVersion: 0,      // bumped by the Classic / New view links so fuUseNewUi re-reads storage
      fuView: 'all',       // the table's view chip (:view.sync), set by the "Due now" card
      fuCounts: null,      // the queue's chip counts + { dueToday, pastDue }
      fuMoreOpen: false,
      fuDialog: null,      // 'create' | 'add' | null
      fuSaving: false,
    };
  },
  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('teamio', ['runQuery']),
    ...mapActions('lists',['createList']),
    ...mapActions('customer', ['getSites', 'getEmployees', 'getSettings']),
    ...mapMutations('teamio', ['emptyResults']),
    ...mapMutations('lists', ['setLists', 'setList']),

    // ---- Follow-ups redesign (new branch only). Each one hands off to the page's EXISTING
    // ---- models and methods (applyFilter → init, saveList, loadList); none re-implements them.

    /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
    fuSetUi(target) {
      if (switchUi('overdueRecall', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
    },

    /** dateFrom / dateTo hold a Date (picker), 'MM/DD/YY' (init) or nothing → 'YYYY-MM-DD'. */
    fuYmd(v) {
      if (!v) return '';
      const m = typeof v === 'string' && /^\d{2}\/\d{2}\/\d{2}$/.test(v) ? moment(v, 'MM/DD/YY') : moment(v);
      return m.isValid() ? m.format('YYYY-MM-DD') : '';
    },

    fuApplyDates({ from, to }) {
      this.dateFrom = moment(from, 'YYYY-MM-DD').toDate();
      this.dateTo = moment(to, 'YYYY-MM-DD').toDate();
      this.applyFilter();
    },

    fuProviderLabel(p) {
      const name = [p.FName, p.MI, p.LName, p.Suffix].filter(x => x && String(x).trim()).join(' ');
      return p.Abbr ? `${name} (${p.Abbr})` : name;
    },

    fuApplyProviders(provNums) {
      const pool = this.fuProviderPool;
      this.selectedProvider = provNums
        .map(n => pool.find(p => String(p.ProvNum) === String(n)))
        .filter(Boolean);
      this.applyFilter();
    },

    fuApplyMore(v) {
      const age = v.age ? v.age.text : '';
      this.patientsOlderThan = age === '' ? null : age;
      const plans = (v.plans && v.plans.selected) || [];
      this.selectedPlans = (this.getPlanOptions || []).filter(p => plans.some(c => String(c) === String(p.CarrierNum)));
      // Only a picked plan needs a mode — an unpicked filter leaves the page's mode as it was.
      if (this.selectedPlans.length) this.includeOrExcludePlan = v.plans.mode;
      const types = (v.types && v.types.selected) || [];
      this.selectedRecallTypes = this.getRecallTypeOptions.filter(rt => types.some(t => String(t) === String(rt.RecallTypeNum)));
      this.fuMoreOpen = false;
      this.applyFilter();
    },

    fuLoadList(list) {
      this.loadList(list.id, list.name);
    },

    fuToggleAll({ rows, checked }) {
      if (checked) this.chooseAll(rows, this.selectedPatients);
      else this.chooseNone(this.selectedPatients);
    },

    // The bulk bar's two buttons. The dialog itself shows the "already on a list" warning and
    // keeps Save disabled (the old modals' Save was disabled by somePatientsBelongsToList too).
    fuOpenListDialog(kind) {
      this.resetError();
      this.fuDialog = kind;
    },

    async fuSaveList({ kind, name, description, listId }) {
      if (this.fuSaving) return;
      this.fuSaving = true;
      try {
        if (kind === 'create') {
          this.listTitle = name;
          this.listDescription = description;
          await this.saveList(null);
        } else {
          this.selectedList = (this.getLists || []).find(l => l.ListID === listId) || [];
          await this.saveList(null, listId);
        }
        // On success saveList reloads the page; on a failure the error shows on the page.
        if (this.errored) this.fuDialog = null;
      } finally {
        this.fuSaving = false;
      }
    },

    async init() {           
      this.busy = true;
      //let dateFrom = this.dateFrom ? moment(this.dateFrom).format('YYYY-MM-DD') :  moment().subtract(6, 'months').format('YYYY-MM-DD');
      //let dateTo = this.dateTo ? moment(this.dateTo).format('YYYY-MM-DD') :  moment().format('YYYY-MM-DD'); 
      
      let dateFrom = this.dateFrom ? moment(this.dateFrom).format('MM/DD/YY') :  moment().subtract(6, 'months').format('MM/DD/YY');
      let dateTo = this.dateTo ? moment(this.dateTo).format('MM/DD/YY') :  moment().format('MM/DD/YY');

      this.dateFrom = dateFrom;
      this.dateTo = dateTo;
      let providers = [];
      let isSecondary = null;
      let insPlans;

      this.resetError();

      if (!this.selectedProvider || this.selectedProvider.length === 0) {
        this.selectedProvider = this.getDefaultProvider;
      }
      for (const p of this.selectedProvider) {
        providers.push(p.ProvNum);
      }

      if(this.patientsOlderThan) {
        console.log('this.patientsOlderThan', this.patientsOlderThan);
        if (isNaN(parseInt(this.patientsOlderThan, 10))) {
          this.$toast.error('Please enter a valid age for Patients older than filter');
          this.busy = false;
          return false;
        }

        let po;
        try {
          po = parseInt(this.patientsOlderThan, 10);
          if(po < 0 || po > 100) {
            this.$toast.error('Please enter a valid age for Patients older than filter');
            this.busy = false;
            return false;
          }
        } catch (error) {
          this.$toast.error('Please enter a valid age for Patients older than filter');
          this.busy = false;
          return false;
        }        
      }

      if(providers.length === 0) {        
        this.$toast.error('Please select one or more Providers');
        this.busy = false;
        return;
      }

      isSecondary = Array.isArray(this.selectedProvider) ? this.selectedProvider[0].IsSecondary : this.selectedProvider.IsSecondary;
      //console.log(`isSecondary: ${isSecondary}`);

      if(this.selectedPlans && this.selectedPlans.length > 0) {

        if(this.includeOrExcludePlan === "") {          
          this.$toast.error('Please select either INCLUDE or EXCLUDE for Plan option before submitting');
          this.busy = false;
          return false;
        }

        insPlans = _.map(this.selectedPlans, function(item) {
          return `${item.CarrierNum}`;
        }).join(',');
        //console.log('Plans:' + insPlans);
      }

      let recallTypes = this.selectedRecallTypes.map(rt => rt.RecallTypeNum);

      let query = {
        type: 'OVERDUE_RECALL',
        siteID: this.siteID,
        listID: 0,
        dateFrom: moment(dateFrom).format('YYYY-MM-DD'),  // have to do this here coz backedn expects it in this format..don't change this
        dateTo: moment(dateTo).format('YYYY-MM-DD'), // have to do this here coz backedn expects it in this format..don't change this
        patientsOlderThan: this.patientsOlderThan,
        providers: providers,
        isSecondary: isSecondary,
        recallTypes: recallTypes.length > 0 ? recallTypes : undefined,
        insPlans: insPlans,
        includeOrExcludePlan: this.includeOrExcludePlan,
        _date: moment().format('YYYY-MM-DD'),
        country: localStorage.getItem('country') || 'USA'
      };

      this.emptyResults();
      try {
        this.results = await this.runQuery(query);
        this.busy = false;
      } catch (err) {
        this.busy = false;
      }
    },

    searchPlans(s) { 
      s = s.trim();      
      if(s !== '') {
        if (s.indexOf('*') > -1) {
          let str = s.substring(0,s.length-1);
          //console.log('str', str);          
          for(let option of this.getPlanOptions) {
            //console.dir(option);
            if(option.CarrierName.toUpperCase().startsWith(str.toUpperCase())) {
              //console.dir(option);
              this.selectedPlans.push(option);
            }
          }
        }
      }
    },

    isEmpty(obj) {
      for(var key in obj) {
          if(obj.hasOwnProperty(key))
              return false;
      }
      return true;
    },

    applyFilter() {
      //console.log('applying filter');
      let diff = moment(this.dateTo).diff(moment(this.dateFrom), 'months', true);
      if(diff > 24) {
        this.$toast.error('You can only filter with maximum of 24 months difference between the dates. Please try again.');        
        return false;  
      }
      this.init();
    },

    loadList(listID, name) {
      this.$router.push({
        name: 'OverdueRecallList',
        params: {
          listID: listID,
        }
      });
    },

    async saveList(event, listID=null) {
      let payload;

      if(listID === null) {
        if(this.listTitle === '') {
          this.$toast.error('Please provide a list name');          
          return false;     
        }

        payload = {
          Name : this.listTitle,
          Description: this.listDescription,
          CreatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
          CreatedByEmpID: this.userID,
          //AssignedToEmpID: this.listAssignedTo.EmployeeID,
          AssignedToEmpID: this.userID,   // changed this after we did multiple employee set up
          TaskTypeID: 3, // OVERDUE RECALL
          SiteID: this.siteID,
          FilterDates: this.dateFrom && this.dateTo ? 'From ' + moment(this.dateFrom).format('MM-DD-YYYY') + ' to ' + moment(this.dateTo).format('MM-DD-YYYY') : '',
          FilterProvider: this.selectedProvider.length > 0 ? `${this.selectedProvider.FName} ${this.selectedProvider.LName}` : '',
          FilterCarriers: this.selectedPlans.length > 0 ?_.map(this.selectedPlans, 'CarrierName').join(',') : '',
          listPatients: this.selectedPatients,
        };
      } else {
        // There's an existing list to which the user is adding patients
        if(this.selectedPatients.length === 0) {
          this.$toast.error('You do not have any patients selected. Please close this window and select patients first');          
          return false;  
        }
        payload = {
          ListID: listID,
          TaskTypeID: 3, // OVERDUE RECALL
          SiteID: this.siteID,
          FilterDates: this.dateFrom && this.dateTo ? 'From ' + moment(this.dateFrom).format('MM-DD-YYYY') + ' to ' + moment(this.dateTo).format('MM-DD-YYYY') : '',
          FilterProvider: this.selectedProvider.length > 0 ? `${this.selectedProvider.FName} ${this.selectedProvider.LName}` : '',
          FilterCarriers: this.selectedPlans.length > 0 ?_.map(this.selectedPlans, 'CarrierName').join(',') : '',
          listPatients: this.selectedPatients,
        };
      }   

      //console.log('payload:' + JSON.stringify(payload));

      try {
        await this.createList(payload);
        this.$bvModal.hide('create-list');
        //location.reload();
        if(!this.errored) {
          this.$router.go({
            path: this.$router.path,
            query: {
              t: + new Date()
            }
          });
        }
      } catch (err) {
          console.error(err);
      }
    },

    removeCode(option) {
      let removeIdx = this.selectedCodes.findIndex(each => {
        return each.ProcCode === option.ProcCode;
      });
      this.selectedCodes.splice(removeIdx, 1);
    },

    removeProvider(option) {
      let removeIdx = this.selectedProvider.findIndex(each => {
        return each.ProvNum === option.ProvNum;
      });
      this.selectedProvider.splice(removeIdx, 1);
    },

    removePlan(option) {
      let removeIdx = this.selectedPlans.findIndex(each => {
        return each.CarrierNum === option.CarrierNum;
      });
      this.selectedPlans.splice(removeIdx, 1);
    },

    hideModal(ref) {
      this.$refs[ref].hide();
    },

    /*
    initCharts() {
      // eslint-disable-line
      $("#easy-pie1").easyPieChart({
        barColor: "#8fe5d4",
        trackColor: "#f8f9fa",
        scaleColor: false,
        lineWidth: 10,
        size: 120
      });
    },
    */

    chooseNone() {
      this.selectedPatients = [];
    },

    handleMainCheckbox() {
      this.isMainChecked ? this.chooseAll(this.dataToDisplay[0], this.selectedPatients) : this.chooseNone(this.selectedPatients);
    },

    // Patient selection methods (used by AdminTable) - match other implementations
    updateSelectedPatients(selectedPatients, patient) {
      const index = selectedPatients.findIndex(p => p.PatNum === patient.PatNum);
      if (index > -1) {
        selectedPatients.splice(index, 1);
      } else {
        selectedPatients.push(patient);
      }
    },

    isSelectedPatient(selectedPatients, patNum) {
      return selectedPatients.some(p => p.PatNum === patNum);
    },

    chooseAll(data, selectedPatients) {
      // Clear existing selection first
      selectedPatients.splice(0, selectedPatients.length);
      // Add all patients
      data.forEach(patient => {
        selectedPatients.push(patient);
      });
    },

    chooseNone(selectedPatients) {
      selectedPatients.splice(0, selectedPatients.length);
    },

    codeWithDesc({ ProcCode, Descript }){
      return `${ProcCode} — [${Descript}]`;
    },

    providerCustomLabel({FName, MI, LName, Suffix, Abbr}) {
      return `${FName} ${MI} ${LName} ${Suffix} (${Abbr})`;
    },

    planOptionsWithCount({CarrierName, totalplans}) {
      return `${CarrierName} (${totalplans})`;
    },

    employeesCustomLabel({Name}) {
      return `${Name}`;
    },

    successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
    },

    callGoalsUpdated() {
      //console.log(`callGoalsUpdated called`);
      if (this.dataToDisplay && 
          this.dataToDisplay.length > 0 && 
          this.dataToDisplay[4] && 
          this.dataToDisplay[4].length > 0 &&
          this.dataToDisplay[5] && 
          this.dataToDisplay[5].length > 0 &&
          this.dataToDisplay[6] && 
          this.dataToDisplay[6].length > 0) {
        Vue.set(this.dataToDisplay[4][0], 'dailyAttempts', this.dataToDisplay[4][0].dailyAttempts + 1);
        Vue.set(this.dataToDisplay[5][0], 'weeklyAttempts', this.dataToDisplay[5][0].weeklyAttempts + 1);
        Vue.set(this.dataToDisplay[6][0], 'monthlyAttempts', this.dataToDisplay[6][0].monthlyAttempts + 1);
      }
    },

    // Mass messaging event handlers (called by AdminTable)
    clearSelection() {
      this.selectedPatients = [];
    }
    
  },

  computed: {
    ...mapState({
        _list: state => state.lists.list,
    }),

    // ---- Follow-ups redesign (new branch only) -------------------------------------------------

    /**
     * Which template branch renders. An office user → STAGE.overdueRecall and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
     */
    fuUseNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('overdueRecall', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    // The tab bar: the header's permission rule; a count only for this page, whose rows are loaded.
    fuTabs() {
      const counts = {};
      if (this.overdueRecallData && this.fuCounts) counts.overdueRecall = this.fuCounts.now;
      return visibleTabs((a, s) => !!(this.$can && this.$can(a, s)), counts);
    },

    // The same numbers the old Call Goals panel shows: attempts / the goal setting.
    fuGoals() {
      if (!this.allSettings) return {};
      const goal = k => (this.allSettings[k] ? this.allSettings[k].PropertyValue : undefined);
      return {
        daily: { done: this.dailyAttempts, goal: goal('OVERDUE_RECALL_DAILY_CALLS_GOAL') },
        weekly: { done: this.weeklyAttempts, goal: goal('OVERDUE_RECALL_WEEKLY_CALLS_GOAL') },
        monthly: { done: this.monthlyAttempts, goal: goal('OVERDUE_RECALL_MONTHLY_CALLS_GOAL') },
      };
    },

    fuToday() {
      return moment().format('YYYY-MM-DD');
    },

    fuDatePopover() {
      return {
        kind: 'date',
        title: 'Recall due date',
        from: this.fuYmd(this.dateFrom),
        to: this.fuYmd(this.dateTo),
        today: this.fuToday,
        width: 330,
        estHeight: 300,
      };
    },

    fuDateLabel() {
      const from = this.fuYmd(this.dateFrom);
      const to = this.fuYmd(this.dateTo);
      if (!from || !to) return 'Recall due';
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      return `Recall due ${moment(from).format(fmt).slice(0, 5)} – ${moment(to).format(fmt)}`;
    },

    // The provider list the old multiselect offers, plus any selected provider it lacks.
    fuProviderPool() {
      const pool = (this.getProviderOptions || []).slice();
      (Array.isArray(this.selectedProvider) ? this.selectedProvider : []).forEach((p) => {
        if (p && !pool.some(o => o && o.ProvNum === p.ProvNum)) pool.push(p);
      });
      return pool.filter(p => p && p.ProvNum !== undefined && p.ProvNum !== null);
    },

    fuSelectedProvNums() {
      return (Array.isArray(this.selectedProvider) ? this.selectedProvider : []).map(p => p.ProvNum);
    },

    fuProvidersPopover() {
      return {
        kind: 'providers',
        title: 'Providers',
        options: this.fuProviderPool.map(p => ({ value: p.ProvNum, label: this.fuProviderLabel(p) })),
        selected: this.fuSelectedProvNums,
        emptyText: 'No providers to pick from yet.',
        minPicksText: 'Pick at least one provider.',
        width: 320,
        estHeight: 300,
      };
    },

    fuProvidersLabel() {
      const n = this.fuSelectedProvNums.length;
      if (!n) return 'Providers';
      if (n === 1) {
        const p = this.fuProviderPool.find(x => x.ProvNum === this.fuSelectedProvNums[0]);
        return p ? this.fuProviderLabel(p) : '1 provider';
      }
      return n === this.fuProviderPool.length ? 'All providers' : `${n} providers`;
    },

    fuMoreSections() {
      return [
        {
          key: 'age',
          title: 'Patient age',
          hint: 'Only patients older than this age.',
          input: { prefix: 'Older than', suffix: 'years', placeholder: 'Any', min: 0, max: 100, errorText: 'Enter an age from 0 to 100.' },
        },
        {
          key: 'plans',
          title: 'Insurance plans',
          modes: [{ value: 'INCLUDE', label: 'Include' }, { value: 'EXCLUDE', label: 'Exclude' }],
          options: (this.getPlanOptions || []).map(p => ({ value: p.CarrierNum, label: p.CarrierName, count: p.totalplans })),
          searchable: true,
          emptyText: 'No insurance plans to pick from yet.',
        },
        {
          key: 'types',
          title: 'Recall types',
          options: this.getRecallTypeOptions.map(rt => ({ value: rt.RecallTypeNum, label: rt.Description })),
          emptyText: 'No recall types to pick from yet.',
        },
      ];
    },

    fuMoreValue() {
      return {
        age: { text: this.patientsOlderThan === null || this.patientsOlderThan === undefined ? '' : String(this.patientsOlderThan) },
        plans: { mode: this.includeOrExcludePlan || 'INCLUDE', selected: (this.selectedPlans || []).map(p => p.CarrierNum) },
        types: { selected: (this.selectedRecallTypes || []).map(rt => rt.RecallTypeNum) },
      };
    },

    fuMoreCount() {
      return (this.patientsOlderThan ? 1 : 0)
        + (this.selectedPlans && this.selectedPlans.length ? 1 : 0)
        + (this.selectedRecallTypes && this.selectedRecallTypes.length ? 1 : 0);
    },

    fuSortedLists() {
      return sortListsNewestFirst(this.getLists || []);
    },

    fuListsPopover() {
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      return {
        kind: 'lists',
        title: 'Overdue recall lists',
        width: 340,
        estHeight: 380,
        align: 'right',
        emptyText: 'No lists yet.',
        lists: this.fuSortedLists.map(l => ({
          id: l.ListID,
          name: l.Name,
          meta: [l.CreatedAt ? `Created ${moment(l.CreatedAt).format(fmt)}` : '', l.EmployeeName || ''].filter(Boolean).join(' · '),
          current: false,
        })),
      };
    },

    fuDialogLists() {
      return this.fuSortedLists.map(l => ({ id: l.ListID, name: l.Name }));
    },

    // The selection as the dialog shows it; `onList` names the list a patient is already on.
    fuDialogPatients() {
      const lists = this.getLists || [];
      return this.selectedPatients.map((p) => {
        let onList = null;
        if (p.BelongsToListID) {
          const l = lists.find(x => x.ListID === p.BelongsToListID);
          onList = (l && l.Name) || p.ListName || 'another list';
        }
        return { id: p.PatNum, name: p.Patient, onList };
      });
    },

    dataToDisplay() {
      if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
    },

    isAptReminderPaidFor() {
      if(this.allSettings && this.allSettings.APPT_REMINDER_PAID_FOR) {
        if (this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === 'TRUE')
          return true;
        else
          return false;
      } else
        return false;
    },

    columns() {
      if(this.isAptReminderPaidFor)
        return ['id', 'Patient', 'LastHygienist', 'DateDue', 'RecallInfo', 'Carrier', 'AutoRemindersSent', 'Status','FollowUpActionPlan', 'Attempts'];
      else
        return ['id', 'Patient', 'LastHygienist', 'DateDue', 'RecallInfo', 'Carrier', 'Status','FollowUpActionPlan', 'Attempts'];
    },

    filterableColumns() {
      if(this.isAptReminderPaidFor)
        return ['Patient', 'LastHygienist', 'Carrier', 'AutoRemindersSent', 'Status', 'Attempts']
      else
        return ['Patient', 'LastHygienist', 'Carrier', 'Status', 'Attempts']
    },



    overdueRecallData() {
      if(this.dataToDisplay) {
        if(this.frequencyof2peryear)
          return this.dataToDisplay[0].filter(a => a.PreventiveFrequency2xPerYear === 'Yes');
        else
          return this.dataToDisplay[0];
      }
    },

    /* Looks at selectedPatients list and if any of them belongs to a list then it will complain */
    somePatientsBelongsToList() {
        if (this.selectedPatients.some(p => p.BelongsToListID && p.BelongsToListID !== null)) {
          return true;
        } else {
          return false;
        }
    },
    
    // Deduped by ProvNum, first occurrence wins so the backend's ordering and the
    // row shape are both preserved. The query can return the same provider more than
    // once, which surfaced as repeated names in the dropdown; because the multiselect
    // is `track-by="ProvNum"`, selecting one of those rows marked EVERY row sharing
    // that ProvNum as selected, so the selected-count jumped by more than one per click.
    getProviderOptions() {
      if(!this.dataToDisplay || !Array.isArray(this.dataToDisplay[1]))
        return [];

      const seen = new Set();
      return this.dataToDisplay[1].filter(p => {
        if(!p || p.ProvNum === undefined || p.ProvNum === null) return true;
        if(seen.has(p.ProvNum)) return false;
        seen.add(p.ProvNum);
        return true;
      });
    },

    getRecallTypeOptions() {
      if(this.dataToDisplay && this.dataToDisplay[7])
        return this.dataToDisplay[7];
      else
        return [];
    },

    getDefaultProvider() {
      if (this.providers && this.allSettings && this.allSettings.OVERDUE_RECALL_DEFAULT_PROVIDER) {
        const provNums = String(this.allSettings.OVERDUE_RECALL_DEFAULT_PROVIDER.PropertyValue)
          .split('|').map(n => parseInt(n, 10)).filter(n => !isNaN(n) && n > 0);
        const matches = _.uniqBy(this.providers.filter(p => provNums.includes(p.ProvNum)), 'ProvNum');
        if (matches.length > 0) return matches;
      }
      return [];
    },

    getPlanOptions() {
      if(this.dataToDisplay)
        return this.dataToDisplay[2];
      else
        return [];
    },

    getLists() {
      /*
      Comment by Nilay: initially we were filtering by whoever this list was assigned to. But we dont want that,
      All lists should be able to be worked on by anyone as long as they're assigned as accountable person for Overdue recall.
      As of now, our list creation process simply groups them into "buckets" like "tina's patients", "dr.k's patients", etc.
      Anyone can work on them, as long as they're assigned to do that type of task!
      */
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[3];
        //return (this.user.Role === 'Admin' || this.user.Role === 'Super Admin' || this.user.Role === 'Manager')    ? this.dataToDisplay[3] : this.dataToDisplay[3].filter(l => l.AssignedToEmpID === this.userID);
    },

    recentLists() {
      if(this.getLists && this.getLists.length > 0) {
        // Sort by CreatedAt descending and take the 3 most recent
        return [...this.getLists]
          .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt))
          .slice(0, 3);
      }
      return [];
    },

    dailyAttempts() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[4] && this.dataToDisplay[4].length > 0)
        return this.dataToDisplay[4][0].dailyAttempts || 0;
      return 0;
    },

    dailyPercent() {
      if(this.dailyAttempts && this.allSettings && this.allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL)
        return Math.round(this.dailyAttempts / this.allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue*100);
      return 0;
    },

    dailyStyle() {
      if(this.dailyPercent)
        return `width: ${this.dailyPercent}%;`;
      return 'width: 0%;';
    },

    weeklyAttempts() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[5] && this.dataToDisplay[5].length > 0)
        return this.dataToDisplay[5][0].weeklyAttempts || 0;
      return 0;
    },

    weeklyPercent() {
      if(this.weeklyAttempts && this.allSettings && this.allSettings.OVERDUE_RECALL_WEEKLY_CALLS_GOAL)
        return Math.round(this.weeklyAttempts / this.allSettings.OVERDUE_RECALL_WEEKLY_CALLS_GOAL.PropertyValue*100);
      return 0;
    },

    weeklyStyle() {
      if(this.weeklyPercent)
        return `width: ${this.weeklyPercent}%;`;
      return 'width: 0%;';
    },

    monthlyAttempts() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[6] && this.dataToDisplay[6].length > 0)
        return this.dataToDisplay[6][0].monthlyAttempts || 0;
      return 0;
    },

    monthlyPercent() {
      if(this.monthlyAttempts && this.allSettings && this.allSettings.OVERDUE_RECALL_MONTHLY_CALLS_GOAL)
        return Math.round(this.monthlyAttempts / this.allSettings.OVERDUE_RECALL_MONTHLY_CALLS_GOAL.PropertyValue*100);
      return 0;
    },

    monthlyStyle() {
      if(this.monthlyPercent)
        return `width: ${this.monthlyPercent}%;`;
      return 'width: 0%;';
    },

    // https://github.com/matfish2/vue-tables-2/blob/master/lib/config/defaults.js
    options() {
      return {
        headings: {
            AutoRemindersSent: 'Auto Reminders Sent',
            LastHygienist: 'Last Hygienist Seen'
          },
          skin:'table',
          sortByColumn: true,
          sortable: ['DateDue', 'Carrier', 'Status', 'Attempts'],
          perPage: 50,
          pagination: { chunk: 10, dropdown: false }, 
          filterByColumn: true,  // https://matanya.gitbook.io/vue-tables-2/list-filters         
          //filterable: ['Patient', 'Carrier', 'AutoRemindersSent', 'Status', 'Attempts'],
          filterable: this.filterableColumns ? this.filterableColumns : [],
          texts: {
            filter: 'Filter by Patient or Carrier or AutoRemindersSent'
            //count:' '
          },
          //columnsClasses: {id:'w'}
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          },
          sortIcon: {
            base: "fa",
            is: "fa-sort",
            up: "fa-sort-asc",  
            down: "fa-sort-desc"
          },
      }
    }

  },

  watch: {
      getDefaultProvider(newValue, oldValue) {
        if (!oldValue || oldValue.length === 0) {
          this.init();
        }
      }
  }
};
</script>

<style lang='scss'>

/* FIX: Checkbox column being clipped on responsive tables */
/* Use padding instead of overflow:visible to avoid layout issues */
.overdue-recall-table-section .card-body,
.unscheduled-treatment-table-section .card-body {
  padding-left: 5px !important;
}

.overdue-recall-table-section .table-responsive,
.unscheduled-treatment-table-section .table-responsive,
.overdue-recall-table-section .overdue-recall-table,
.unscheduled-treatment-table-section .overdue-recall-table {
  margin-left: 0 !important;
  margin-top: 0 !important;
}

.overdue-recall-table-section table,
.unscheduled-treatment-table-section table {
  width: 100% !important;
  table-layout: auto !important;
}

.overdue-recall-table-section table th:first-child,
.overdue-recall-table-section table td:first-child,
.unscheduled-treatment-table-section table th:first-child,
.unscheduled-treatment-table-section table td:first-child {
  min-width: 45px !important;
  width: 45px !important;
  padding-left: 12px !important;
  padding-right: 5px !important;
}

.overdue-recall-table-section .abc-checkbox,
.unscheduled-treatment-table-section .abc-checkbox {
  margin: 0;
  padding: 0;
}

/* This is a fix for when the close button wasn't aligned properly */


.alert .close {
	position: relative !important;
  right: -16px !important;
  top: -16px !important
}
.VuePagination{
  flex-direction: row-reverse;
  float: right;
  margin: 0 !important;
  padding: 0 !important;
}
.abc-checkbox > div{
  display: block !important;
}
.help{
  position: absolute;
  right: 45px;
  top: 50px;
}
  button.close{
    position: relative !important; 
    right: 15px !important;
  }
  .table td{
    padding-top: 5px !important;
  }
.table-responsive .VueTables__table thead tr th{
  margin-left: auto !important;
  margin-right: auto !important;
  font-size: 12px !important;
  vertical-align: top !important;
}

.table-responsive .VueTables__table thead tr th{
  vertical-align: top !important
}

/* Below is to fix the issue where sorting icon was showing up on next line vs next to the column heading */
.table-responsive .VueTables__table .VueTables__heading {
    float: left;
}

table > thead > tr > th{
  text-transform: capitalize !important;
}
.VueTables__search-field{
  display: flex;

  label{
    margin-right: 10px
  }
}
.overdue-recall-table-section .card-body .overdue-recall-table-section-header{
  margin: 10px 0 0 !important;
}

.overdue-recall-table-section .card-body .overdue-recall-table-section-header[data-v-786b417f] {
    display: inline !important; 
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: flex-start;
    margin: 10px 0 35px;
    -webkit-box-align: center;
    -ms-flex-align: center;
}

.multiselect__select{
  max-height: 34px !important;  
}

</style>

<!-- SCOPED on purpose: the block above is global, and this button style has no business
     leaking onto any other page. -->
<style lang="scss" scoped>
.recall-report-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 9px;
  border: 1px solid #dfe4e3;
  background: #ffffff;
  color: #0f7259;
  font-size: 16px;
  line-height: 1;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #e9f6f1;
    border-color: #b9dfd2;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.18);
  }
}
</style>
<style src="./OverdueRecall.scss" lang="scss" scoped />
<!-- The redesign's page-level styles (new branch only) live with the Follow-ups pages. -->
<style src="../Followups/overdue-recall-page.scss" lang="scss" scoped />

